<script setup lang="ts">
import type { Menu } from "@mmh/types"
import { AnimatePresence, Motion } from "motion-v"
import { computed, HTMLAttributes, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { ThemeButton, ThemeImg } from "~/components/theme"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs"
import { useLocation } from "~/composables/locations"
import { specialCase } from "~/lib/case"
import { cn } from "~/lib/utils"

const { class: cl } = defineProps<{
    class?: HTMLAttributes["class"]
}>()

const { location, menuPath } = useLocation()
const route = useRoute()
const router = useRouter()

const groups = computed(() => location.value?.menus || {})
const groupNames = computed(() => {
    const requiredOrder = ["daily", "large-party", "catering", "special"]
    
    const availableCategories = Object.keys(groups.value).filter(g => {
        return (groups.value[g] || []).filter(isMenuActive).length > 0
    })
    
    const ordered = requiredOrder.filter(category => availableCategories.includes(category))
    const additional = availableCategories.filter(category => !requiredOrder.includes(category))
    
    return [...ordered, ...additional]
})
const activeGroupName = computed<string>({
    get: () => {
        const category = route.params.category as string | undefined
        return category && groupNames.value.includes(category)
            ? category
            : (groupNames.value[0] ?? "")
    },
    set: (next: string) => {
        if (!next || next === route.params.category) return
        router.replace(menuPath(next))
    }
})
const activeGroupMenus = computed(() => (groups.value[activeGroupName.value] || []).filter(isMenuActive))
const key = computed(() => `${location.value?.name}-menus`)

const getMenuHoursDisplay = (menu: Menu) => {
    if (!menu?.menuHoursCopy) return []
    
    // First decode HTML entities
    const decoded = menu.menuHoursCopy
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, "\"")
        .replace(/&#39;/g, "'")
    
    // Split by <br> tags first, then clean each line
    const lines = decoded
        .split(/<br\s*\/??>|\r?\n/i)
        .map((line: string) => line.replace(/<[^>]*>/g, "").trim()) // Remove HTML tags from each line
        .filter(Boolean)
    
    return lines.map((line: string) => {
        const match = line.match(/^(.*?)(\d.*)$/)
        if (match) {
            return {
                day: match[1].trim(),
                time: match[2].trim().replace(/\s/g, "").toLowerCase().replace(/:00/g, "")
            }
        }
        return { day: line, time: "" }
    })
}

function isMenuActive(menu: Menu) {
    return menu.showMenu && (!menu.endDate || new Date(menu.endDate) > new Date())
}

watch(
    [() => route.params.category, groupNames],
    ([category, availableGroups]) => {
        if (category && availableGroups.length && !availableGroups.includes(category as string)) {
            router.replace(menuPath())
        }
    },
    { immediate: true }
)
</script>

<template>
    <section
        id="menus"
        tabindex="-1"
        :class="cn('space-y-8 md:space-y-10', cl)"
    > 
        <Tabs
            v-if="groupNames.length > 0"
            v-model:model-value="activeGroupName"
            class="flex flex-col gap-6 md:gap-8"
        >
            <h2 class="max-w-md mx-auto text-3xl md:text-4xl font-headline leading-[2.5rem] md:leading-[2.75rem] text-center text-balance">
                Menus
            </h2>
            <TabsList class="w-full max-w-fit mx-auto p-0 gap-4 sm:gap-10 md:gap-12 bg-transparent flex-wrap justify-center">
                <TabsTrigger
                    v-for="groupName of groupNames"
                    :key="groupName"
                    :value="groupName"
                    tabindex="0"
                    class="relative px-0 shadow-none bg-transparent text-foreground-muted capitalize data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                    layout
                >
                    <span>{{ groupName.replace("-", " ") }}</span>
                    <Motion
                        v-if="activeGroupName === groupName"
                        class="absolute bottom-0 left-0 right-0 h-px bg-foreground"
                        layout-id="underline"
                    />
                </TabsTrigger>
            </TabsList>
            <TabsContent
                v-for="groupName of groupNames"
                :key="groupName"
                :value="groupName"
                :tabindex="-1"
            >
                <AnimatePresence mode="wait">
                    <Motion
                        :key="key"
                        class="flex flex-col md:flex-row flex-wrap items-center justify-center gap-4 md:gap-6"
                        initial="hidden"
                        while-in-view="visible"
                        :in-view-options="{ once: true }"
                        :transition="{ 
                            staggerChildren: 0.25
                        }"
                    >
                        <Motion 
                            v-for="menu of activeGroupMenus" 
                            :key="menu.key || menu.name"
                            :variants="{
                                hidden: { x: -25, opacity: 0 },
                                visible: { x: 0, opacity: 1 }
                            }"
                            :transition="{
                                type: 'spring',
                                duration: 1.5
                            }"
                            class="w-full md:w-[calc(50%-theme(spacing.3))]"
                        >
                            <a
                                :href="menu.path || '#'"
                                target="_blank"
                                rel="noopener noreferrer"
                                tabindex="0"
                                :aria-label="`View ${specialCase(menu.name)} menu${getMenuHoursDisplay(menu).length ? ', ' + getMenuHoursDisplay(menu).map(p => `${p.day} ${p.time}`).join(', ') : ''}`"
                                class="relative group cursor-pointer overflow-hidden rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background block"
                            >
                                <div class="relative aspect-[4/2] w-full overflow-hidden">
                                    <ThemeImg
                                        :src="menu.imageUrl!" 
                                        alt=""
                                        class="absolute inset-0 w-full h-full !min-h-0 !object-cover brightness-50 opacity-75 group-hover:brightness-100 group-hover:scale-105 transition-[filter,transform] duration-300"
                                        loading="lazy"
                                        sizes="100vw"
                                    />
                                    <div class="absolute inset-0 flex flex-col items-center justify-center">
                                        <ThemeButton
                                            size="lg"
                                            variant="ghost"
                                            class="text-sm sm:text-base md:text-lg uppercase text-menu-section-copy bg-menu-section-button-background group-hover:bg-menu-section-button-hover-background group-hover:text-menu-section-hover-copy pointer-events-none whitespace-normal text-center leading-tight h-auto py-2"
                                            tabindex="-1"
                                            aria-hidden="true"
                                        >
                                            <span class="capsize">{{ specialCase(menu.name) }}</span>
                                        </ThemeButton>
                                    </div>
                                </div>
                                <div
                                    v-if="getMenuHoursDisplay(menu).length"
                                    class="flex items-center justify-center gap-6 py-3 text-xs font-button font-medium"
                                >
                                    <div
                                        v-for="pair of getMenuHoursDisplay(menu)"
                                        :key="pair.day + pair.time"
                                        class="flex flex-col items-center gap-1"
                                    >
                                        <span class="text-center">{{ pair.day }}</span>
                                        <span class="text-center">{{ pair.time }}</span>
                                    </div>
                                </div>
                            </a>
                        </Motion>
                    </Motion>
                </AnimatePresence>
            </TabsContent>
        </Tabs>
        <p
            class="text-center italic"
            role="note"
        >
            We are a cashless restaurant.
        </p>
    </section>
</template>